import React, { useEffect, useState } from "react";
import { Tooltip, Input } from "antd";
import {
  useAllOrders,
  useSearchOrders,
  useNdrReattempt,
} from "../../../../api/orders/orders"; // Adjust path if needed
import { useLoader } from "../../../../utils/LoaderContext"; // Adjust path if needed
import NdrReattemptModal from "../NdrOrders/Ndr/NdrReattemptModal"; // Adjust this import path if it's incorrect

const ORDERS_PER_PAGE = 20;

const Ndr = ({ status = "ndr" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showReattemptModal, setShowReattemptModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { setLoading } = useLoader();

  // Fetch all orders by status
  const {
    data,
    isLoading,
    refetch: refetchAll,
  } = useAllOrders({
    page: currentPage,
    status,
    search: searchQuery ? "" : undefined,
    startDate: undefined,
    endDate: undefined,
  });

  // Search orders (this hook will be enabled only when searchQuery has a value)
  const { data: searchData, isLoading: isSearchLoading } = useSearchOrders(
    searchQuery,
    status
  );

  const { mutate: reattemptOrder, isLoading: reattempting } = useNdrReattempt();

  // Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Refetch all orders when search query is cleared
useEffect(() => {
  setLoading(isLoading || isSearchLoading);
}, [isLoading, isSearchLoading, setLoading]);


  // Determine which data to use (search results or all orders)
  const orders = searchQuery ? searchData || [] : data?.orders || [];
  const filteredOrders = orders.filter(
    (order) => order.shippingStatusText === status
  );

  const totalOrdersCount = searchQuery
    ? filteredOrders.length
    : data?.totalOrders || 0;
  const totalPages = Math.ceil(totalOrdersCount / ORDERS_PER_PAGE);

  const paginatedOrders = searchQuery
    ? filteredOrders
    : filteredOrders.slice(
      (currentPage - 1) * ORDERS_PER_PAGE,
      currentPage * ORDERS_PER_PAGE
    );

  const isLoadingCombined = isLoading || (searchQuery && isSearchLoading);

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1); // Reset page on search text change
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Manifest ID</th>
              <th className="p-3">Order ID / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Address</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoadingCombined ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-400">
                  No NDR orders found.
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-blue-600 font-medium">
                    {order.shipment_id || "-"}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : "bg-yellow-100 text-yellow-600"
                        }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>
                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1 inline-block">
                      {order.PaymentType}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.Name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                  <td className="p-4">
                    <Tooltip title="Reattempt Order">
                      <button
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowReattemptModal(true);
                        }}
                      >
                        Reattempt
                      </button>
                    </Tooltip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!searchQuery && !isLoadingCombined && totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
                }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* NdrReattemptModal Component - CRITICAL: Add the key prop here */}
      {showReattemptModal && selectedOrder && (
        <NdrReattemptModal
          key={selectedOrder._id}
          showReattemptModal={showReattemptModal}
          setShowReattemptModal={setShowReattemptModal}
          selectedOrder={selectedOrder}

          // 👇 PASS ORDER ID TO MODAL
          orderId={selectedOrder._id}

          reattemptOrder={reattemptOrder}
          reattempting={reattempting}
        />

      )}
    </div>
  );
};

export default Ndr;
